import { useState } from "react";
import { Link } from "react-router-dom";

interface UseCase {
  id: string;
  title: string;
  tagline: string;
  description: string;
  color: string;
  icon: React.ReactNode;
  features: { title: string; description: string; endpoint: string }[];
  flow: string[];
}

const useCases: UseCase[] = [
  {
    id: "payouts",
    title: "Payouts",
    tagline: "Send value instantly across currencies and borders",
    description:
      "Enable your platform to send payments globally across fiat, stablecoins, and Bitcoin. Settle cross-border payments in seconds, not days.",
    color: "ls-sky-500",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
      </svg>
    ),
    features: [
      { title: "Customer Onboarding", description: "Create customers with KYC verification", endpoint: "POST /customers" },
      { title: "Internal Accounts", description: "Auto-provisioned multi-currency wallets", endpoint: "GET /customers/internal-accounts" },
      { title: "External Accounts", description: "Link bank accounts, crypto wallets, UPI, PIX, CLABE", endpoint: "POST /customers/external-accounts" },
      { title: "Cross-Currency Quotes", description: "Real-time FX rates with locked pricing", endpoint: "POST /quotes" },
      { title: "Transfer Out", description: "Send to bank accounts, UMA addresses, crypto wallets", endpoint: "POST /transfer-out" },
      { title: "Transfer In", description: "Pull funds from external accounts", endpoint: "POST /transfer-in" },
    ],
    flow: [
      "Create customer with KYC",
      "Platform provisions internal accounts",
      "Link external destination accounts",
      "Get a quote with locked exchange rate",
      "Execute quote to initiate transfer",
      "Receive webhook on completion",
    ],
  },
  {
    id: "ramps",
    title: "Ramps",
    tagline: "Convert between crypto and fiat seamlessly",
    description:
      "On-ramp fiat to crypto and off-ramp crypto to fiat. Support self-custody wallets with bank account linking via Plaid.",
    color: "emerald-400",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5-13.5L16.5 7.5m0 0L12 3m4.5 4.5V21" />
      </svg>
    ),
    features: [
      { title: "Plaid Integration", description: "One-click bank account linking", endpoint: "POST /plaid/link-token" },
      { title: "Fiat to Crypto", description: "Convert USD/EUR to BTC/USDC/USDT", endpoint: "POST /transfer-out" },
      { title: "Crypto to Fiat", description: "Off-ramp crypto to bank accounts", endpoint: "POST /transfer-in" },
      { title: "Self-Custody Support", description: "Send to Solana, Base, Polygon, Tron wallets", endpoint: "POST /customers/external-accounts" },
      { title: "KYC Onboarding", description: "Automated identity verification", endpoint: "POST /customers" },
      { title: "Real-Time Rates", description: "Locked exchange rates for 5 minutes", endpoint: "POST /quotes" },
    ],
    flow: [
      "Onboard customer with KYC",
      "Link bank account via Plaid",
      "Customer selects crypto to purchase",
      "Get real-time quote",
      "Execute purchase (fiat in, crypto out)",
      "Deliver crypto to self-custody wallet",
    ],
  },
  {
    id: "rewards",
    title: "Rewards & Cashback",
    tagline: "Deliver micro-payouts at scale",
    description:
      "Power cashback, referral bonuses, and reward programs. Optimized for high-volume, low-value transactions with instant settlement.",
    color: "orange-400",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
      </svg>
    ),
    features: [
      { title: "Bulk Payouts", description: "Send thousands of micro-payments efficiently", endpoint: "POST /transfer-out" },
      { title: "Multi-Currency Rewards", description: "Pay in USD, BTC, USDC, or local currency", endpoint: "POST /quotes" },
      { title: "UMA Delivery", description: "Send rewards via Universal Money Addresses", endpoint: "GET /receiver/uma/{address}" },
      { title: "Lightning Speed", description: "Bitcoin Lightning for instant sub-cent payments", endpoint: "POST /transfer-out" },
      { title: "Webhook Notifications", description: "Real-time delivery confirmations", endpoint: "POST /webhooks/test" },
      { title: "Transaction Tracking", description: "Full audit trail for compliance", endpoint: "GET /transactions" },
    ],
    flow: [
      "User earns reward/cashback",
      "Platform triggers payout via API",
      "Grid creates locked quote",
      "Transfer executes instantly",
      "User receives via UMA/Lightning/wallet",
      "Webhook confirms delivery",
    ],
  },
  {
    id: "p2p",
    title: "Global P2P",
    tagline: "Accept funds via bank transfers, wallets, or UMAs",
    description:
      "Enable peer-to-peer remittances with Universal Money Addresses. Users send and receive across borders with simple $user@domain addresses.",
    color: "purple-400",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
      </svg>
    ),
    features: [
      { title: "UMA Addresses", description: "Human-readable payment addresses ($alice@app.com)", endpoint: "GET /receiver/uma/{address}" },
      { title: "Invitation System", description: "Invite users via UMA with referral codes", endpoint: "POST /invitations" },
      { title: "Payment Approval", description: "Review and approve/reject incoming payments", endpoint: "POST /transactions/{id}/approve" },
      { title: "Multi-Rail Support", description: "Bank transfers, crypto, and UMA in one flow", endpoint: "POST /transfer-out" },
      { title: "Receiver Lookup", description: "Resolve UMA addresses before sending", endpoint: "GET /receiver/uma/{address}" },
      { title: "Real-Time Settlement", description: "Instant cross-border settlement", endpoint: "POST /quotes" },
    ],
    flow: [
      "User creates UMA address ($user@app.com)",
      "Sender enters recipient UMA",
      "Grid resolves receiver and shows quote",
      "Sender confirms and payment initiates",
      "Receiver approves incoming payment",
      "Funds settle to receiver's account",
    ],
  },
];

export default function UseCases() {
  const [active, setActive] = useState("payouts");
  const uc = useCases.find((u) => u.id === active)!;

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-ls-gray-50 tracking-tight">
          Use Cases
        </h1>
        <p className="text-sm text-ls-gray-500 mt-1">
          Explore Grid API capabilities across four core use cases
        </p>
      </div>

      {/* Use Case Tabs */}
      <div className="flex items-center gap-1 bg-ls-gray-975 border border-ls-gray-900 rounded-lg p-1 mb-8 w-fit">
        {useCases.map((u) => (
          <button
            key={u.id}
            onClick={() => setActive(u.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
              active === u.id
                ? "bg-ls-gray-800 text-ls-gray-50"
                : "text-ls-gray-500 hover:text-ls-gray-300"
            }`}
          >
            <span className={active === u.id ? `text-${u.color}` : ""}>
              {u.icon}
            </span>
            {u.title}
          </button>
        ))}
      </div>

      {/* Active Use Case Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Overview */}
        <div className="lg:col-span-2">
          <div className="bg-ls-gray-975 border border-ls-gray-900 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-12 h-12 rounded-xl bg-${uc.color}/10 flex items-center justify-center text-${uc.color}`}>
                {uc.icon}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-ls-gray-50">{uc.title}</h2>
                <p className="text-sm text-ls-gray-400">{uc.tagline}</p>
              </div>
            </div>
            <p className="text-sm text-ls-gray-400 leading-relaxed mt-4">
              {uc.description}
            </p>
          </div>

          {/* API Features Grid */}
          <h3 className="text-sm font-semibold text-ls-gray-50 mb-4">
            Key API Capabilities
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {uc.features.map((f, i) => (
              <div
                key={i}
                className="bg-ls-gray-975 border border-ls-gray-900 rounded-lg p-4 hover:border-ls-gray-700 transition-colors"
              >
                <div className="text-sm font-medium text-ls-gray-50 mb-1">
                  {f.title}
                </div>
                <p className="text-xs text-ls-gray-500 mb-3">{f.description}</p>
                <code className="text-[11px] font-mono px-2 py-1 rounded bg-ls-gray-900 text-ls-sky-500">
                  {f.endpoint}
                </code>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Integration Flow */}
        <div>
          <div className="bg-ls-gray-975 border border-ls-gray-900 rounded-lg p-6 sticky top-8">
            <h3 className="text-sm font-semibold text-ls-gray-50 mb-5">
              Integration Flow
            </h3>
            <div className="space-y-0">
              {uc.flow.map((step, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 bg-${uc.color}/10 text-${uc.color}`}>
                      {i + 1}
                    </div>
                    {i < uc.flow.length - 1 && (
                      <div className="w-px h-8 bg-ls-gray-900" />
                    )}
                  </div>
                  <div className="pb-6">
                    <div className="text-sm text-ls-gray-300 pt-1">{step}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-ls-gray-900">
              <Link
                to="/transfer"
                className="btn-primary w-full text-center text-sm"
              >
                Try it Live
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
